/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Breadcrumbs,
    FlexRow,
    FlexCol,
    Heading,
    Icon,
    SecurityHelper,
    StickyBottomHelper,
    Text,
    ModalActions,
    ButtonGroup,
    StyledModal as Modal,
    Alert,
    Spinner,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { authModalActions } from 'shared-ui/auth/redux';
import {
    withRecaptchaFallback,
    RecaptchaCheckbox,
    isRecaptchaChallengeMessage,
} from 'shared-ui/recaptcha';
import { Footer } from 'site-kit';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { gnoment } from 'shared-ui/gnoment';
import size from 'lodash/size';
import isEqual from 'lodash/isEqual';
import {
    AVAILABILITY_KEY_MAP_FORMAT,
    AUTOSELECT_DIVISION,
    CAROLINA_SANDHILLS_ID,
    SOMERVILLE_LAKE_ID,
} from 'ui-permit/src/constants';
import DivisionsMapContainer from 'ui-permit/shared-components/venuePage/DivisionsMapContainer';
import { readRuleAsBoolean } from 'ui-permit/src/utils/utilsNew';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import { INVENTORY_PERMIT } from 'ui-field-sales/src/constants/types';

import {
    availabilityActions,
    permitActions,
    reservationActions,
    seasonActions,
} from '../actions';
import {
    getPermitTypeName,
    getStartAndEndDates,
    canSelectDate,
    isOpenPermitType,
} from '../utils';

import { RES_ID_DELIMITER } from '../constants';
import {
    availabilitySelectors,
    permitSelectors,
    reservationSelectors,
    getErrors,
} from '../reducers';
import {
    venuePageRoute,
    availabilityPageRoute,
    orderDetailsPageRoute,
} from '../../dev/routesArray';

import DateSelector from '../components/DateSelector';
import GroupSizeSelector from '../components/GroupSizeSelector';
import HuntType from '../components/HuntType';
import HuntingAvailability from '../components/HuntingAvailability';
import { LOCATION_TZ } from '../constants/hunting-permit-constants';
import { readMaxGroupSizeRule } from '../constants/registration';
import { getSpecialConfigPermitsDefaultDivisionID } from '../utils/availability_utils';

export class AvailabilityPage extends Component {
    static defaultProps = {};

    static propTypes = {
        account: PropTypes.object,
        availability: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        errors: PropTypes.object,
        addReservation: PropTypes.func,
        dates: PropTypes.array,
        error: PropTypes.object,
        fetchAvailability: PropTypes.func,
        fetchPermit: PropTypes.func,
        fetchUnpaidReservation: PropTypes.func,
        getAvailabilityStartDate: PropTypes.func,
        issuance: PropTypes.object,
        permit: PropTypes.object,
        permitDate: PropTypes.string,
        recaptcha: PropTypes.object,
        reservation: PropTypes.object,
        router: COMPAT_ROUTER_PROP_TYPES,
        openLoginModal: PropTypes.func,
        updateReservation: PropTypes.func,
        fetchSeasons: PropTypes.func,
    };

    state = {
        autoselecting: false,
        date: this.props.router.params.date,
        groupSize: +this.props.router.params.groupSize,
        huntType: this.props.router.params.type,
        permitId: this.props.router.params.permitId,
        isLoadingNext: false,
        isChallengeModalOpen: false,
        selectedDivision: {},
        lastSubmittedDate: null,
    };

    componentDidMount() {
        const { permitId } = this.state;

        this.props.fetchPermit(permitId);
        this.getAvailability();

        const reservationId = this.props.router.searchParams.get('reservation_id');
        if (reservationId) {
            this.props.fetchUnpaidReservation(permitId, reservationId);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { account, permit } = this.props;
        const { autoselecting, permitId, huntType, date, groupSize } = this.state;
        const reservationId = this.props.router.searchParams.get('reservation_id');

        if (!prevProps.account) {
            if (account && autoselecting) {
                this.book();
            }
        }

        if (date) {
            window.history.replaceState(
                {},
                '',
                availabilityPageRoute(permitId, huntType, date, groupSize, reservationId)
            );
        }
        if (prevState.huntType !== huntType) {
            this.props.fetchAvailability({ permitId, type: huntType }).then(() => {
                if (this.props.dates.length === 1) {
                    this.setState({ date: this.props.dates[0].split('/')[0] });
                }
            });
        }

        // Auto-select single open permit type
        if (
            isOpenPermitType(permit, huntType) &&
            date &&
            !reservationId &&
            !prevState.selectedDivision.id &&
            isEqual(prevState, this.state)
        ) {
            // setState is OK with non-conflicting conditional
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(
                { autoselecting: true, selectedDivision: { id: 'ANY_DIVISION' } },
                this.book
            );
        }
    }

    getAvailability = async () => {
        const { permitId, huntType } = this.state;
        const currentDate = gnoment
            .tz(LOCATION_TZ[permitId])
            .format(AVAILABILITY_KEY_MAP_FORMAT);
        try {
            const seasons = await this.props.fetchSeasons({ permitId, currentDate });
            const year = seasons?.[0]?.season_year;
            this.props.fetchAvailability({ permitId, type: huntType, year });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Season error', error);
        }
    };

    // Closing the challenge modal abandons the in-flight attempt, so reset the
    // state that gates a retry: `autoselecting` (otherwise an auto-booked open
    // permit falls back to an endless spinner) and `lastSubmittedDate` (which
    // keeps the Book Now button disabled). The auto-book won't re-fire because
    // a division is now selected.
    closeChallengeModal = () => {
        this.setState({
            isChallengeModalOpen: false,
            autoselecting: false,
            lastSubmittedDate: null,
        });
    };

    book = async () => {
        const { account, availability, getAvailabilityStartDate, permit, dates } =
            this.props;
        const { permitId, huntType, date, groupSize, selectedDivision } = this.state;
        if (!account) {
            this.props.openLoginModal();
            return;
        }
        this.setState({ isLoadingNext: true, lastSubmittedDate: date });
        const cantSelectDate = !canSelectDate(permit, huntType);
        const isOpen = isOpenPermitType(permit, huntType);
        let divisionId = isOpen ? 'ANY_DIVISION' : selectedDivision.id;

        // for this special config to work the permit_type must be "open: true"
        if (
            readRuleAsBoolean(AUTOSELECT_DIVISION, permit.rules) ||
            [CAROLINA_SANDHILLS_ID, SOMERVILLE_LAKE_ID].includes(permit.id) // TODO: this must be removed when migrated Hunting to Rules
        ) {
            divisionId = getSpecialConfigPermitsDefaultDivisionID(
                permit.id,
                availability,
                date
            );
        }

        const isYouthHunt = /youth\b.*\bhunt/.test(huntType);
        const isDisabledHunt = /disabled/.test(huntType);
        const reservationId = this.props.router.searchParams.get('reservation_id') ?? '';
        const isModifyQuota = !!reservationId;
        const [startDate, endDate] = getStartAndEndDates(dates);

        const reservation = {
            id: reservationId,
            extra_fields: {
                group_size: groupSize,
                is_modify_quota: isModifyQuota,
                availability_start_date: cantSelectDate
                    ? getAvailabilityStartDate(huntType)
                    : date,
            },
            division_id: divisionId,
            // $FlowFixMe // The following syntax is fine
            group_members: Array(groupSize).fill({
                ...(isDisabledHunt && { pass_type: 'Interagency Access Pass' }),
            }),
            permit_id: permitId,
            permit_type: huntType,
            end_date: cantSelectDate ? endDate || date : date,
            start_date: cantSelectDate ? startDate || date : date,
            user_id: SecurityHelper.getAccount().account_id,
        };

        if (reservationId) {
            reservation.group_members.forEach(
                (g, i) =>
                    (reservation.group_members[i] =
                        this.props.reservation?.group_members?.[i] || {})
            );
        } else if (!isYouthHunt) {
            reservation.group_members[0] = {
                ...reservation.group_members[0],
                ...SecurityHelper.getAccount(),
            };
        }

        const isBackBtnClicked = this.props.router.navigationType === 'POP';
        //If it doesn't have detailed availability and back btn clicked just go back to facility page
        if (isOpenPermitType(permit, huntType) && isBackBtnClicked) {
            this.props.router.navigate(`/permits/${this.props.router.params.permitId}`);
            return;
        }

        const { recaptcha } = this.props;

        // Only the create path (addReservation) is gated. Modify-quota
        // (updateReservation) requires an existing issuance and is left ungated.
        if (!isModifyQuota) {
            if (recaptcha.isChallengeRequired && !this.state.isChallengeModalOpen) {
                this.setState({ isChallengeModalOpen: true, isLoadingNext: false });
                return;
            }

            // getToken throws if the reCAPTCHA script failed to load. Don't block
            // the reservation on it - submit without a token; the backend decides.
            let recaptchaToken = null;
            try {
                recaptchaToken = await recaptcha.getToken('create_issuance');
            } catch {
                recaptchaToken = null;
            }
            reservation.recaptcha_token = recaptchaToken;
            reservation.recaptcha_version = recaptcha.isChallengeRequired ? 'V2' : 'V3';
        }

        this.props[isModifyQuota ? 'updateReservation' : 'addReservation'](reservation)
            .then(({ id, issuance }) => {
                this.setState({ isChallengeModalOpen: false });
                this.props.router.navigate(
                    orderDetailsPageRoute(
                        permitId,
                        `${isModifyQuota ? issuance.id : id}${RES_ID_DELIMITER}${permitId}`
                    )
                );
                this.setState({ isLoadingNext: false });
            })
            .catch((e) => {
                this.setState({ isLoadingNext: false });
                const { shouldRetry } = this.props.recaptcha.handleRecaptchaError(e);
                if (shouldRetry) {
                    // Score too low - pop the v2 challenge modal for the retry.
                    this.setState({ isChallengeModalOpen: true });
                }
            });
    };

    render() {
        const { availability, permit, errors: rawErrors, dates } = this.props;
        const {
            huntType,
            date,
            groupSize,
            autoselecting,
            lastSubmittedDate,
            isChallengeModalOpen,
        } = this.state;
        const { name, id, rules } = permit;

        // reCAPTCHA challenge signals arrive as reservation errors but are
        // surfaced through the challenge modal, so keep them out of the
        // page-level error display.
        const errors = Object.fromEntries(
            Object.entries(rawErrors).filter(
                ([, message]) => !isRecaptchaChallengeMessage(message)
            )
        );

        // Open permits auto-book (autoselecting) and normally render nothing
        // while doing so, but the reCAPTCHA challenge modal must still appear
        // when the backend requires it.
        if ((!name || autoselecting) && !size(errors) && !isChallengeModalOpen) {
            // Show a spinner while auto-booking so the user isn't left staring
            // at a blank screen until the reCAPTCHA challenge modal appears.
            return autoselecting ? <Spinner isCentered size="xl" /> : null;
        }

        const availabilityForSelectedDate = availability.find(
            (d) => d.start_date === date
        );
        const reservationId = this.props.router.searchParams.get('reservation_id');
        const permitTypeName = getPermitTypeName(permit, huntType);
        const cantSelectDate = !canSelectDate(permit, huntType);
        const isOpen = isOpenPermitType(permit, huntType);
        const continueText = reservationId ? 'Update Reservation' : 'Book Now';
        const maxGroupSize = readMaxGroupSizeRule(id, huntType, rules);
        const hideGroupSize = maxGroupSize === 1;
        const continueButton = (
            <Button
                isLoading={this.state.isLoadingNext}
                appearance="primary"
                size={isOpen ? 'md' : 'lg'}
                aria-label={continueText}
                onClick={this.book}
                disabled={
                    (!isOpen && !this.state.selectedDivision.id) ||
                    isEqual(date, lastSubmittedDate)
                }
            >
                {continueText}
            </Button>
        );

        const hasExistingReservation = Object.values(errors).find(
            (e) => /existing reservation/.test(e) || /You may only register/.test(e)
        );

        const isRestricted = Object.values(errors).find(
            (e) =>
                /forbidden: After careful review/.test(e) ||
                /you have been disqualified from making any further reservations/.test(e)
        );

        if (hasExistingReservation || isRestricted) {
            return (
                <Modal
                    shouldShowCloseButton={false}
                    shouldActionsStickInMobile
                    size="md"
                    isOpen={true}
                    heading="Error Creating Reservation"
                >
                    <Text>{errors.reservation}</Text>

                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                appearance="link"
                                onClick={() =>
                                    this.props.router.navigate(venuePageRoute(permit.id))
                                }
                            >
                                Go Back
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </Modal>
            );
        }

        return (
            <div className="hunting-permit per-availability-page">
                {/* Fire the view_item GA Event */}
                <SendGAEvent
                    itemLevel="Registration Detail"
                    itemCategory={INVENTORY_PERMIT}
                    facilityId={permit.id}
                />

                <div className="per-availability-layout">
                    <div className="per-availability-main" id="per-availability-main">
                        <div className="per-availability-main-contents">
                            <div className="hunting-permit-availability-config">
                                <div
                                    role="menu"
                                    tabIndex={0}
                                    className="per-availability-filters"
                                >
                                    {!reservationId && (
                                        <div
                                            className="filter-item filter-input"
                                            role="menuitem"
                                        >
                                            <HuntType
                                                isLabelVisible={false}
                                                permitTypesList={permit.permit_types_list}
                                                permitID={permit.id}
                                                value={huntType}
                                                onChange={(v) =>
                                                    this.setState({
                                                        huntType: v,
                                                        date: '',
                                                    })
                                                }
                                            />
                                        </div>
                                    )}

                                    {!cantSelectDate && (
                                        <div
                                            className="filter-item filter-input"
                                            role="menuitem"
                                        >
                                            <DateSelector
                                                isLabelVisible={false}
                                                dates={dates}
                                                value={date}
                                                onChange={(v) =>
                                                    this.setState({
                                                        date: v,
                                                        isLoadingNext: false,
                                                    })
                                                }
                                            />
                                        </div>
                                    )}

                                    {!hideGroupSize && (
                                        <div
                                            className="filter-item filter-input group-size-selector"
                                            role="menuitem"
                                        >
                                            <GroupSizeSelector
                                                permit={permit}
                                                permitType={huntType}
                                                isLabelVisible={false}
                                                value={groupSize}
                                                onChange={(v) =>
                                                    this.setState({ groupSize: v })
                                                }
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="per-availability-title">
                                    <div className="title-navigation">
                                        <Breadcrumbs
                                            path={[
                                                { title: 'Home', url: '/' },
                                                {
                                                    title: name,
                                                    url: `/permits/${permit.id}`,
                                                },
                                                { title: 'Detailed Availability' },
                                            ]}
                                        />
                                    </div>

                                    <Heading headingLevel={1} appearance="h4">
                                        Detailed Availability - {permitTypeName}
                                    </Heading>

                                    <Heading
                                        headingLevel={2}
                                        appearance="h5"
                                        className="mb-3"
                                    >
                                        {name}
                                    </Heading>
                                </div>

                                {permit.id && !isOpen && (
                                    <HuntingAvailability
                                        availability={availabilityForSelectedDate}
                                        selectedGroupSize={this.state.groupSize}
                                        selectedDivision={this.state.selectedDivision}
                                        selectedStartDate={this.state.date}
                                        permitDate={this.state.date}
                                        permit={permit}
                                        onSelectToggle={(selectedDivision) =>
                                            this.setState({ selectedDivision })
                                        }
                                        startDate={gnoment(this.props.permitDate)}
                                        isExternal
                                    />
                                )}
                            </div>

                            {isOpen ? (
                                <div className="per-availability-book-now-wrapper">
                                    <FlexRow>
                                        <FlexCol>
                                            {!!size(errors) && (
                                                <div aria-live="polite">
                                                    <Alert
                                                        className="m-2"
                                                        type="error"
                                                        hasAriaLive
                                                    >
                                                        {errors.reservation}
                                                    </Alert>
                                                </div>
                                            )}
                                        </FlexCol>
                                    </FlexRow>

                                    <FlexRow className="info-wrapper">
                                        <FlexCol>
                                            <Icon iconName="info-outline" size="xl" />
                                            <Heading
                                                headingLevel={2}
                                                appearance="h5"
                                                className="mb-2"
                                            >
                                                Open Hunt Days
                                            </Heading>
                                            <p>
                                                Open hunt days do not require you to
                                                select a specific unit in advance.
                                            </p>
                                            <p>
                                                Select{' '}
                                                <strong>
                                                    &apos;{continueText}&apos;
                                                </strong>{' '}
                                                to proceed with your open hunt
                                                reservation.
                                            </p>
                                        </FlexCol>
                                    </FlexRow>

                                    <FlexRow className="button-wrapper">
                                        <FlexCol>{continueButton}</FlexCol>
                                    </FlexRow>
                                </div>
                            ) : (
                                <StickyBottomHelper
                                    scrollingContainerId="per-availability-main"
                                    key={!!size(errors)}
                                >
                                    <div className="per-availability-book-now">
                                        <div className="per-availability-book-now-content">
                                            {continueButton}
                                        </div>
                                        {!!size(errors) &&
                                            isEqual(date, lastSubmittedDate) && (
                                                <div aria-live="polite">
                                                    <Alert type="error" hasAriaLive>
                                                        {errors.reservation}
                                                    </Alert>
                                                </div>
                                            )}
                                    </div>
                                </StickyBottomHelper>
                            )}
                        </div>
                    </div>

                    <div className="per-availability-aside">
                        <div className="per-availability-map" id="map">
                            <DivisionsMapContainer
                                permit={permit}
                                selectedDivision={this.state.selectedDivision}
                                permitType="hunting-permit"
                                fitFeatures
                            />
                        </div>
                    </div>
                </div>
                <Footer inline />
                {this.state.isChallengeModalOpen && (
                    <Modal
                        isOpen={this.state.isChallengeModalOpen}
                        onRequestClose={this.closeChallengeModal}
                        heading="Confirm You're Not a Robot"
                    >
                        Please complete the challenge below to continue with your
                        reservation.
                        <RecaptchaCheckbox
                            {...this.props.recaptcha.recaptchaCheckboxProps}
                            isFallbackOnly
                            action="create_issuance"
                        />
                        <ModalActions>
                            <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                                <Button
                                    appearance="tertiary"
                                    onClick={this.closeChallengeModal}
                                    isDisabled={this.state.isLoadingNext}
                                    gaTrackingId="hunting-issuance-recaptcha-cancel"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={this.book}
                                    isDisabled={this.props.recaptcha.isV2Invalid}
                                    isLoading={this.state.isLoadingNext}
                                    gaTrackingId="hunting-issuance-recaptcha-submit"
                                >
                                    Continue
                                </Button>
                            </ButtonGroup>
                        </ModalActions>
                    </Modal>
                )}
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        addReservation: bindActionCreators(reservationActions.addReservation, dispatch),
        fetchAvailability: bindActionCreators(
            availabilityActions.fetchAvailability,
            dispatch
        ),
        fetchPermit: bindActionCreators(permitActions.fetchPermit, dispatch),
        fetchUnpaidReservation: bindActionCreators(
            reservationActions.fetchUnpaidReservation,
            dispatch
        ),
        openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
        updateReservation: bindActionCreators(
            reservationActions.updateReservation,
            dispatch
        ),
        fetchSeasons: bindActionCreators(seasonActions.fetchSeasons, dispatch),
    };
}

export default withCompatRouter(
    connect((state) => {
        return {
            availability: availabilitySelectors.getAvailability(state),
            permit: permitSelectors.getPermit(state),
            errors: getErrors(state, 'reservation'),
            dates: availabilitySelectors.getDates(state),
            getAvailabilityStartDate:
                availabilitySelectors.getAvailabilityStartDate(state),
            reservation: reservationSelectors.getReservation(state),
            account: state.auth.account,
            permitDate: state.huntingpermit.permitDate,
        };
    }, mapDispatchToProps)(withRecaptchaFallback(AvailabilityPage))
);

// cSpell:ignore SANDHILLS, autoselecting, huntingpermit
